'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import React, { type ButtonHTMLAttributes, type ReactNode, type CSSProperties } from 'react';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-gradient-to-r from-[#8d610c] to-[#c69632] text-white hover:from-[#7a5409] hover:to-[#b5862d] focus:ring-[#c69632]',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500',
        outline: 'border-2 border-[#c69632] text-[#c69632] bg-transparent hover:bg-[#c69632] hover:text-white focus:ring-[#c69632]',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      },
      size: {
        small: 'text-sm px-3 py-1.5',
        medium: 'text-base px-4 py-2',
        large: 'text-lg px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
  text?: string;
  text_font_size?: string;
  text_font_weight?: string;
  text_line_height?: string;
  text_text_align?: string;
  text_color?: string;
  fill_background?: string;
  border_border_radius?: string;
  effect_box_shadow?: string;

  fill_background_color?: string;
  border_border?: string;
  text_text_transform?: string;
  layout_width?: string;
  padding?: string;
  position?: string;
  margin?: string;
  layout_gap?: string;

  children?: ReactNode;
}

const Button = ({
  text = "Contact Us",
  text_font_size = "text-md",
  text_font_weight = "font-medium",
  text_line_height = "leading-md",
  text_text_align = "center",
  text_color = "text-white",
  fill_background = "linear-gradient(333deg,#8d610c 0%, #c69632 100%)",
  border_border_radius = "rounded-5xl",
  effect_box_shadow = "", // ✅ shadow removed

  fill_background_color,
  border_border,
  text_text_transform,
  layout_width,
  padding,
  position,
  margin,
  layout_gap,

  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: ButtonProps) => {
  const optionalClasses = [
    layout_width ? `w-[${layout_width}]` : '',
    padding ? `p-[${padding}]` : '',
    margin ? `m-[${margin}]` : '',
    position || '',
    layout_gap ? `gap-[${layout_gap}]` : '',
    text_text_transform || '',
    border_border || '',
    fill_background_color ? `bg-[${fill_background_color}]` : '',
  ].filter(Boolean).join(' ');

  const customStyles: CSSProperties = {
    fontFamily: "BDO Grotesk, sans-serif",
    ...(fill_background.includes('gradient') && { background: fill_background }),
  };

  const styleClasses = [
    text_font_size,
    text_font_weight,
    text_line_height,
    `text-${text_text_align}`,
    text_color,
    !variant && !fill_background.includes('gradient') ? `bg-[${fill_background}]` : '',
    border_border_radius,
    effect_box_shadow, // ✅ empty by default
  ].filter(Boolean).join(' ');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (typeof onClick === 'function') {
      onClick(event);
    }
  };

  return (
    <button data-component-id="src/components/ui/Button.tsx:119:4" data-component-path="src/components/ui/Button.tsx" data-component-line="119" data-component-end-line="134" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={customStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        styleClasses,
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;
